'use client';

import { getSiteConfig } from '@shared/config';
import { twMerge } from '@shared/themes';
import { ROUTES, useIsMobile } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { ContentSwitcher } from '../../form-controls/ContentSwitcher/ContentSwitcher';
import type { Option } from '../../form-controls/types';
import { InnerPageContainer } from '../../InnerPageContainer/InnerPageContainer';
import { Link } from '../../Link/Link';
import { SecondaryHeaderClient } from '../../SecondaryHeaderContainer/SecondaryHeaderContainer';
import { PurchasePod } from '../PurchasePod/PurchasePod';
import { PurchasesEmptyState } from '../PurchasesEmptyState/PurchasesEmptyState';
import type { LotteryName, PurchasedTicketsState, TicketPurchased } from '../types';
import { LegacySitePurchasesNotification } from './components/LegacySitePurchasesNotification';
import { LoadingMoreSkeleton } from './components/LoadingMoreSkeleton';
import { LoadingPodsSkeleton } from './components/LoadingPodsSkeleton';
import { usePurchasedTickets } from './hooks/usePurchasedTickets';
import { usePurchasedTicketsScrollRestore, usePurchasedTicketsTabs } from './hooks/usePurchasedTicketsTabs';

type PurchasedTicketsProps = ComponentProps<'div'> & {
  className?: string;
  lotteries?: LotteryName[];
};

const PURCHASE_CONTAINER_CLASSES = {
  GRID: 'll-grid-container gap-y-6 pb-10 sm-md:pt-10 sm-md:[--ll-container-padding:40px]',
  INNER: 'll-container-inner flex flex-col gap-3',
};

function PurchasedTickets({ className, lotteries, ...props }: PurchasedTicketsProps) {
  const t = useTranslations('lotteries.purchase');
  const PURCHASE_OPTIONS: Option<PurchasedTicketsState>[] = [
    { label: t('open_bets'), value: 'open', trackingProps: { 'data-track-name': 'Open Bets' }, 'data-testid': 'open-bets' },
    { label: t('previous_bets'), value: 'closed', trackingProps: { 'data-track-name': 'Previous Bets' }, 'data-testid': 'previous-bets' },
  ];
  const isMobile = useIsMobile({ breakpoint: 'sm-md' });
  const { showsTicketStateSwitcher, showsLegacySiteNotification } = getSiteConfig().LOTTERY_PURCHASES;
  const { selectedOption, ticketState: selectedTicketState, handleStateChange, handlePodClick, returnState } = usePurchasedTicketsTabs(PURCHASE_OPTIONS);
  // Without the switcher, all tickets are fetched and listed together.
  const ticketState = showsTicketStateSwitcher ? selectedTicketState : undefined;
  const showsSettledContent = ticketState !== 'open';
  const trackContainer = { open: 'Open Bets', closed: 'Previous Bets', all: 'All Bets' }[ticketState ?? 'all'];

  const { data, isLoading, error, isFetchingNextPage, hasNextPage, loadMoreRef } = usePurchasedTickets({
    ticketState,
    type: 'ticket',
    restriction: 'all',
    filter: { lottery: lotteries ?? [] },
    sort: { submissionDate: 'descending' },
  });

  usePurchasedTicketsScrollRestore(returnState, !isLoading && data != null);

  if (!isLoading && (error || !data)) return null;

  const betsStatusSwitcher = showsTicketStateSwitcher ? (
    <div className="ll-container-inner w-full">
      <ContentSwitcher id="purchases-state" options={PURCHASE_OPTIONS} value={selectedOption} onChange={handleStateChange} size="large" fullWidth />
    </div>
  ) : null;

  const legacySiteNotification = showsLegacySiteNotification ? (
    <LegacySitePurchasesNotification className="ll-container-inner" data-track-type="Legacy Bets" data-track-message="Not Available" />
  ) : null;

  if (!isLoading && data.length === 0) {
    return (
      <InnerPageContainer className={className}>
        <div className={twMerge(PURCHASE_CONTAINER_CLASSES.GRID, 'pt-6')}>
          {betsStatusSwitcher}
          {legacySiteNotification}
          <div className={PURCHASE_CONTAINER_CLASSES.INNER} data-track-container={trackContainer} {...props}>
            <PurchasesEmptyState />
          </div>
        </div>
      </InnerPageContainer>
    );
  }

  return (
    <div data-track-feature="Lotteries" data-track-section="Purchases">
      {isMobile && betsStatusSwitcher ? <SecondaryHeaderClient>{betsStatusSwitcher}</SecondaryHeaderClient> : null}

      <InnerPageContainer className={className} {...props}>
        <div className={PURCHASE_CONTAINER_CLASSES.GRID}>
          {!isMobile ? betsStatusSwitcher : null}
          {legacySiteNotification}
          {isLoading ? (
            <LoadingPodsSkeleton showDetailsRow={showsSettledContent} />
          ) : (
            <div className={PURCHASE_CONTAINER_CLASSES.INNER} data-track-container={trackContainer}>
              {data.map(({ ticketId, lotteryName, totalWinning, numberOfLines, renewType, cancelReceiptAt, ticketState: purchasedTicketState }: TicketPurchased) => (
                <PurchasePod
                  key={ticketId}
                  ticketId={ticketId}
                  lotteryName={lotteryName}
                  winnings={totalWinning}
                  numberOfLines={numberOfLines}
                  renewType={renewType}
                  cancelReceiptAt={cancelReceiptAt}
                  ticketState={purchasedTicketState}
                  onClick={handlePodClick}
                  data-track-container="Bet"
                  data-track-product={lotteryName}
                  data-track-lines={numberOfLines}
                  {...(purchasedTicketState === 'CLOSED' && { 'data-track-status': totalWinning > 0 ? 'Win' : 'No Win' })}
                />
              ))}

              {(hasNextPage || isFetchingNextPage) && (
                <div ref={loadMoreRef} className={`flex flex-col gap-3 ${hasNextPage ? 'min-h-15' : ''}`}>
                  {isFetchingNextPage ? <LoadingMoreSkeleton /> : null}
                </div>
              )}
            </div>
          )}
          {showsSettledContent && !hasNextPage ? (
            <div className="ll-container-inner flex flex-col items-center gap-2 mt-6" data-track-container="Older Transactions">
              <p>{t('older_transactions')}</p>
              <Link href={ROUTES.HELP_CENTRE.BASE_URL} target="_blank" rel="noopener" data-testid="help-link" data-track-name="Help">
                {t('get_help')}
              </Link>
            </div>
          ) : null}
        </div>
      </InnerPageContainer>
    </div>
  );
}

export { PURCHASE_CONTAINER_CLASSES, PurchasedTickets };
